import { ref, shallowRef } from "vue";

interface DebounceContext {
  onReset: () => void;
  timeout: ReturnType<typeof setTimeout>;
}

export function useAwaitAction<P extends readonly unknown[], V>(
  fn: (...args: readonly [...P]) => Promise<V>,
  options: {
    debounce?: number;
  } = {},
) {
  const { debounce: debounceDelay } = options;

  const loading = ref(false);
  const error = ref<unknown>(null);
  const result = shallowRef<V | null>(null);

  const debounceContext = ref<DebounceContext | null>(null);

  async function handler(...args: readonly [...P]) {
    loading.value = true;
    error.value = null;
    result.value = null;

    try {
      result.value = await fn(...args);
    } catch (e) {
      console.error(e);
      error.value = e ?? null;
    } finally {
      loading.value = false;
    }
  }

  function resetContext() {
    if (debounceContext.value !== null) {
      clearTimeout(debounceContext.value.timeout);
      debounceContext.value.onReset();
      debounceContext.value = null;
    }
  }

  function debouncedHandler(...args: readonly [...P]): Promise<void> {
    if (!debounceDelay) return handler(...args);

    if (debounceContext.value !== null) {
      resetContext();
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        handler(...args).then(resolve);
      }, debounceDelay);

      debounceContext.value = {
        onReset: resolve,
        timeout,
      };
    });
  }

  return {
    handler: debouncedHandler,
    loading,
    error,
    result,
  };
}
